import { useState } from 'react';
import { LeftArrow } from '@benzinga/themed-icons';
import { User } from '@benzinga/session';
import { UserSubscription } from '@benzinga/subscription-manager';
import { useSupportChat } from '@benzinga/intercom-ui';
import { OptionButton } from './components';

interface ContactFlowProps {
  setShowContact: React.Dispatch<React.SetStateAction<boolean>>;
  setShowInitial: React.Dispatch<React.SetStateAction<boolean>>;
  handleClose: () => void;
  subscription: UserSubscription;
  user: User | null;
}

export const ContactFlow: React.FC<ContactFlowProps> = ({
  handleClose,
  setShowContact,
  setShowInitial,
  subscription,
  user,
}) => {
  const [contactMethod, setContactMethod] = useState<'email' | 'sms' | 'phone' | ''>('');
  const [contactTime, setContactTime] = useState<'now' | 'later' | ''>('');
  const [showRequested, setShowRequested] = useState(false);
  const [showError, setShowError] = useState(false);
  const { openSupport } = useSupportChat();

  const handleSupportSubmit = () => {
    const message = `Customer Support Request (Cancel Workflow)
Contact Method: ${contactMethod}
Contact Time: ${contactTime === 'now' ? 'ASAP' : 'Please connect to schedule a time.'}
Subscription Name: ${subscription.planName}
Subscription UUID: ${subscription.uuid}
Subscription Total Price: ${subscription.totalPrice}
User Email: ${user?.email}
Phone: ${user?.phoneNumber}`;

    if (openSupport(message)) {
      setShowRequested(true);
    } else {
      setShowError(true);
    }
  };

  return (
    <div className="h-full w-full">
      {!showRequested && !showError && (
        <div className="flex flex-col gap-2 h-full">
          <div>
            <h2>Contact Us</h2>
            <p className="text-xs m-0">Our support team can help you with any problems you might be facing</p>
          </div>
          <div className="pt-2">
            <div>What is your preferred channel of communication?</div>
            <div className="flex flex-col justify-between items-start gap-2 text-sm pt-2">
              <OptionButton
                index={0}
                label="Email"
                selectedVal={contactMethod}
                setMethod={setContactMethod}
                value="email"
              />
              <OptionButton
                index={1}
                label="SMS"
                selectedVal={contactMethod}
                setMethod={setContactMethod}
                value="sms"
              />
              <OptionButton
                index={2}
                label="Phone Call"
                selectedVal={contactMethod}
                setMethod={setContactMethod}
                value="phone"
              />
            </div>
          </div>
          {contactMethod && (
            <div className="pt-2">
              <div>Would you like to get assistance now?</div>
              <div className="flex flex-col justify-between items-start gap-2 text-sm pt-2">
                <OptionButton
                  index={0}
                  label="Join Queue now"
                  selectedVal={contactTime}
                  setMethod={setContactTime}
                  value="now"
                />
                <OptionButton
                  index={1}
                  label="Set Time for Communication"
                  selectedVal={contactTime}
                  setMethod={setContactTime}
                  value="later"
                />
              </div>
            </div>
          )}
          {contactTime && contactMethod && (
            <div className="h-full flex items-end">
              <button
                className="bg-bzorange-500 text-white px-4 py-2 mx-auto mt-4 w-fit rounded-sm"
                onClick={handleSupportSubmit}
              >
                Complete Request
              </button>
            </div>
          )}
          <button
            className="absolute top-2 left-2"
            onClick={() => {
              setShowContact(false);
              setShowInitial(true);
            }}
          >
            <LeftArrow />
          </button>
        </div>
      )}
      {showRequested && (
        <div className="flex flex-col justify-between h-full">
          <h2>Support Requested</h2>
          <img alt="help is on the way gif" src="https://media.giphy.com/media/keTQt1Ry7yM9dlwDkp/giphy.gif" />
          <div>
            <p className="pt-2">A Benzinga Research Expert is on the way...</p>
            <p className="">
              We will reach out to you via {contactMethod}{' '}
              {contactTime === 'now' ? 'as soon as possible.' : 'to set a time that works best for you.'}
            </p>
          </div>
          <button className="p-2 border border-bzgrey-400/80 shadow-sm rounded-md w-fit" onClick={handleClose}>
            Close
          </button>
        </div>
      )}
      {showError && (
        <div className="flex flex-col justify-between h-full">
          <h2>Support Request</h2>
          <div>
            <p className="pt-2">There was an issue completing your request.</p>
            <p className="">
              Please visit our{' '}
              <a href="/contact" target="_blank">
                Contact Us
              </a>{' '}
              page to see different ways to reach out to us.
            </p>
            <p>
              Feel free to call us directly at <a href="tel:+18774409464">1 (877) 440-9464</a> or email us at{' '}
              <a href="mailto:support@benzinga.com">support@benzinga.com</a>
            </p>
          </div>
          <button className="p-2 border border-bzgrey-400/80 shadow-sm rounded-md w-fit" onClick={handleClose}>
            Close
          </button>
        </div>
      )}
    </div>
  );
};
